import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import VehicleHistoryPanel from "@/components/VehicleHistoryPanel";
import { Loader2, Activity, Bike, IndianRupee, Gauge, Users, ArrowLeft, Wrench, ClipboardCheck, MapPin, Clock, TrendingUp, AlertTriangle, ShieldCheck, Route } from "lucide-react";

const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const d = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const ACTION_LABEL = {
  booking_created: "Booking created", kyc_passed: "KYC passed", agreement_signed: "Agreement signed",
  gps_paired: "GPS paired", handover_complete: "Handed over (active)", handover_blocked: "Handover blocked",
  vehicle_returned: "Vehicle returned", rental_closed: "Rental closed & settled",
  tourist_booking_created: "Tourist booking", tourist_marked_paid: "Tourist marked paid",
  tourist_payment_confirmed: "Payment confirmed", tourist_payment_rejected: "Payment rejected",
  work_order_open: "Work order opened", work_order_in_progress: "Maintenance started", work_order_done: "Maintenance completed",
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className={`mt-1.5 text-2xl font-bold tracking-tight ${accent || ""}`}>{value}</p>
    </div>
  );
}

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/operator/analytics/overview").then(({ data }) => setO(data)); }, []);
  if (!o) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const a = o.alerts;
  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2 text-sm font-semibold text-muted-foreground">Fleet pulse</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Stat icon={Bike} label="Fleet size" value={o.fleet.total} />
          <Stat icon={Activity} label="Utilisation" value={`${o.fleet.utilisation_pct}%`} accent="text-tenant-primary" />
          <Stat icon={ClipboardCheck} label="Active rentals" value={o.fleet.active_rentals} />
          <Stat icon={Wrench} label="In maintenance" value={o.fleet.maintenance} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-muted-foreground">Today</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={Route} label="Trips today" value={o.today.trips} />
          <Stat icon={Gauge} label="Distance today" value={`${o.today.distance_km} km`} />
          <Stat icon={IndianRupee} label="Revenue today" value={inr(o.today.revenue)} accent="text-status-available" />
          <Stat icon={Activity} label="Vehicles online" value={o.today.active_vehicles} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-muted-foreground">Live alerts</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={Clock} label="Idle too long" value={a.idle} accent={a.idle ? "text-status-overdue" : ""} />
          <Stat icon={AlertTriangle} label="Out of zone" value={a.breach} accent={a.breach ? "text-status-recovery" : ""} />
          <Stat icon={TrendingUp} label="Speeding" value={a.speeding} accent={a.speeding ? "text-status-recovery" : ""} />
          <Stat icon={AlertTriangle} label="Stale GPS" value={a.stale} accent={a.stale ? "text-status-overdue" : ""} />
        </div>
      </div>
    </div>
  );
}

function VehicleDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [trips, setTrips] = useState(null);
  useEffect(() => {
    api.get(`/operator/analytics/vehicles/${id}`).then(({ data }) => setData(data));
    api.get(`/operator/analytics/vehicles/${id}/trips`).then(({ data }) => setTrips(data));
  }, [id]);
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const v = data.vehicle;
  return (
    <div className="space-y-5">
      <button data-testid="analytics-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">{v.reg_number} <span className="text-base font-normal text-muted-foreground">· {v.model}</span></h2>
            {v.address && <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {v.address}</p>}
          </div>
          <span className="rounded-full bg-tenant-primary/10 px-3 py-1 text-xs font-semibold capitalize text-tenant-primary">{v.status}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={Gauge} label="Total km" value={(v.total_km || 0).toLocaleString()} />
          <Stat icon={ClipboardCheck} label="Rentals" value={data.stats.rentals} />
          <Stat icon={Users} label="Unique riders" value={data.stats.unique_riders} />
          <Stat icon={Route} label="Trips today" value={trips ? trips.trips.length : "…"} />
        </div>
      </div>

      {trips && trips.trips.length > 0 && (
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <p className="mb-3 text-sm font-semibold">Trips today · {trips.total_distance_km} km over {trips.total_duration_min} min</p>
          <div className="space-y-2">
            {trips.trips.map((t, i) => (
              <div key={i} data-testid={`trip-${i}`} className="flex items-center justify-between rounded-tenant-button border border-tenant-border px-3 py-2 text-sm">
                <span className="text-muted-foreground">{new Date(t.start).toLocaleTimeString()} → {new Date(t.end).toLocaleTimeString()}</span>
                <span className="font-medium">{t.distance_km} km · {t.duration_min}m · max {t.max_speed} km/h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <VehicleHistoryPanel vehicleId={id} />
      </div>

      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <p className="mb-3 text-sm font-semibold">Audit history</p>
        {data.timeline.length === 0 ? <p className="text-sm text-muted-foreground">No events yet.</p> : (
          <ol className="relative space-y-4 border-l border-tenant-border pl-5">
            {data.timeline.map((e, i) => (
              <li key={i} data-testid={`audit-event-${i}`} className="relative">
                <span className={`absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full ${e.kind === "maintenance" ? "bg-status-overdue" : "bg-tenant-primary"}`} />
                <p className="text-sm font-medium">{ACTION_LABEL[e.action] || e.action}</p>
                <p className="text-xs text-muted-foreground">{dt(e.ts)}{e.customer ? ` · ${e.customer}` : ""}{e.actor_role ? ` · ${e.actor_role}` : ""}</p>
                {e.detail && <p className="mt-0.5 text-xs text-muted-foreground">{Object.entries(e.detail).filter(([, val]) => val != null).map(([k, val]) => `${k}: ${typeof val === "object" ? JSON.stringify(val) : val}`).join(" · ")}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function Vehicles({ onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/operator/analytics/vehicles").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((v) => (
        <button key={v.id} data-testid={`analytics-vehicle-${v.reg_number}`} onClick={() => onOpen(v.id)}
          className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 text-left shadow-tenant-soft transition-all hover:-translate-y-0.5 hover:shadow-tenant-hover">
          <div className="flex items-center justify-between">
            <p className="font-semibold">{v.reg_number}</p>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider capitalize text-muted-foreground">{v.status}</span>
          </div>
          <p className="text-sm text-muted-foreground">{v.model}</p>
          <div className="mt-3 flex justify-between text-xs text-muted-foreground">
            <span>{(v.total_km || 0).toLocaleString()} km</span>
            <span>{v.rentals} rentals</span>
            <span>{v.last_active ? new Date(v.last_active).toLocaleDateString() : "—"}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function CustomerDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/operator/analytics/customers/${id}`).then(({ data }) => setData(data)); }, [id]);
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const p = data.profile;
  return (
    <div className="space-y-5">
      <button data-testid="analytics-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">{p.name}</h2>
            <p className="text-sm text-muted-foreground">{p.phone} · <span className="capitalize">{p.segment}</span> · since {d(p.first_seen)}</p>
          </div>
          <div className="flex items-center gap-2">
            {p.blocklist_status === "clear"
              ? <span className="inline-flex items-center gap-1 rounded-full bg-status-available/15 px-3 py-1 text-xs font-semibold text-status-available"><ShieldCheck className="h-3.5 w-3.5" /> Clear</span>
              : <span className="rounded-full bg-status-recovery/15 px-3 py-1 text-xs font-semibold text-status-recovery">{p.blocklist_status}</span>}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={ClipboardCheck} label="Rentals" value={p.total_bookings} />
          <Stat icon={Gauge} label="Total km" value={(p.total_km || 0).toLocaleString()} />
          <Stat icon={IndianRupee} label="Total spend" value={inr(p.total_spend)} accent="text-status-available" />
          <Stat icon={Route} label="Avg trip" value={`${p.avg_trip_km} km`} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={Bike} label="Favourite" value={p.favourite_model || "—"} />
          <Stat icon={Clock} label="Avg duration" value={`${p.avg_duration_hrs} h`} />
          <Stat icon={Activity} label="Active days" value={p.active_days} />
          <Stat icon={ShieldCheck} label="Clean returns" value={p.clean_rate_pct != null ? `${p.clean_rate_pct}%` : "—"} accent={p.incidents ? "text-status-overdue" : ""} />
        </div>
      </div>
      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <p className="mb-3 text-sm font-semibold">Rental history</p>
        <div className="space-y-2">
          {data.history.map((h) => (
            <div key={h.id} className="flex items-center justify-between rounded-tenant-button border border-tenant-border px-3 py-2 text-sm">
              <div><span className="font-medium">{h.vehicle_reg}</span> · <span className="text-muted-foreground">{h.vehicle_model}</span></div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="capitalize">{h.stage}</span>
                {h.km_used != null && <span>{h.km_used} km</span>}
                <span>{d(h.created_at)}</span>
              </div>
            </div>
          ))}
          {data.history.length === 0 && <p className="text-sm text-muted-foreground">No rentals yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Customers({ onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/operator/analytics/customers").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (rows.length === 0) return <p className="py-10 text-center text-sm text-muted-foreground">No customers yet.</p>;
  return (
    <div className="overflow-hidden rounded-tenant-card border border-tenant-border bg-tenant-surface shadow-tenant-soft">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr><th className="px-4 py-2.5 font-medium">Customer</th><th className="px-4 py-2.5 font-medium">Rentals</th><th className="px-4 py-2.5 font-medium">Total km</th><th className="px-4 py-2.5 font-medium">Spend</th><th className="px-4 py-2.5 font-medium">Favourite</th><th className="px-4 py-2.5 font-medium">Last active</th></tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} data-testid={`analytics-customer-${c.id}`} onClick={() => onOpen(c.id)} className="cursor-pointer border-t border-tenant-border transition-colors hover:bg-muted/30">
              <td className="px-4 py-3"><p className="font-medium">{c.name}</p><p className="text-xs capitalize text-muted-foreground">{c.segment} · {c.phone}</p></td>
              <td className="px-4 py-3">{c.total_bookings}</td>
              <td className="px-4 py-3">{(c.total_km || 0).toLocaleString()} km</td>
              <td className="px-4 py-3 font-medium">{inr(c.total_spend)}</td>
              <td className="px-4 py-3">{c.favourite_model || "—"}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{d(c.last_active)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OperatorAnalytics() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("overview");
  const vehicleId = params.get("vehicle");
  const customerId = params.get("customer");

  const open = useCallback((key, id) => { setParams(id ? { [key]: id } : {}); }, [setParams]);
  const back = () => setParams({});

  useEffect(() => {
    if (vehicleId) setTab("vehicles");
    else if (customerId) setTab("customers");
  }, [vehicleId, customerId]);

  const TABS = [["overview", "Overview"], ["vehicles", "Vehicles"], ["customers", "Customers"]];

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">History & analytics</h1>
        <p className="mt-1 text-sm text-muted-foreground">Fleet utilisation, per-vehicle audit history & daily trips, and customer usage profiles.</p>
      </div>

      <div className="inline-flex rounded-tenant-card border border-tenant-border bg-tenant-surface p-1 shadow-tenant-soft">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`analytics-tab-${k}`} onClick={() => { setTab(k); back(); }}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-tenant-primary text-tenant-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "vehicles" && (vehicleId ? <VehicleDetail id={vehicleId} onBack={back} /> : <Vehicles onOpen={(id) => open("vehicle", id)} />)}
      {tab === "customers" && (customerId ? <CustomerDetail id={customerId} onBack={back} /> : <Customers onOpen={(id) => open("customer", id)} />)}
    </div>
  );
}
